import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";
import { ADMIN_PERMISSION_KEYS, getStoredAdmin } from "../utils/adminAuth";

const permissionLabels = {
  dashboard: "Dashboard",
  users: "Users",
  deposits: "Deposits",
  withdraws: "Withdraws",
  results: "Results",
  markets: "Markets",
  stake_analysis: "Stake Analysis",
  settings: "Settings",
  google_auth: "Google Auth",
  banners: "Banners",
  promotions: "Promotions",
  notifications: "Notifications",
  charts: "Charts",
  agents: "Agents",
  casino: "Casino",
  expenses: "Expenses",
  sub_admins: "Sub Admins",
  resets: "System Reset",
};

const buildPermissionState = (source = {}) => ADMIN_PERMISSION_KEYS.reduce((acc, key) => {
  acc[key] = source[key] !== false;
  return acc;
}, {});

function AdminSubAdmins() {
  const admin = getStoredAdmin();
  const [rows, setRows] = useState([]);
  const [fullAdminRows, setFullAdminRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fullAdminLoading, setFullAdminLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState("");
  const [fullAdminForm, setFullAdminForm] = useState({
    id: "",
    name: "",
    email: "",
    password: "",
    status: "Active",
    is_main_admin: false,
  });
  const [form, setForm] = useState({
    id: "",
    name: "",
    email: "",
    password: "",
    is_active: true,
    permissions: buildPermissionState(),
  });
  const [lastSecret, setLastSecret] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState("");

  const canManage = admin?.role === "admin";
  const canManageFullAdmins = canManage && (Number(admin?.id) === 10 || String(admin?.email || "").trim().toLowerCase() === "admin@gmail.com");

  const loadRows = async () => {
    setLoading(true);
    try {
      const res = await API.get("/admin/sub-admins");
      setRows(Array.isArray(res.data?.sub_admins) ? res.data.sub_admins : []);
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to load sub admins");
    } finally {
      setLoading(false);
    }
  };

  const loadFullAdmins = async () => {
    if (!canManageFullAdmins) return;
    setFullAdminLoading(true);
    try {
      const res = await API.get("/admin/full-admins");
      setFullAdminRows(Array.isArray(res.data?.admins) ? res.data.admins : []);
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to load full admins");
    } finally {
      setFullAdminLoading(false);
    }
  };

  useEffect(() => {
    if (canManage) {
      loadRows();
      if (canManageFullAdmins) {
        loadFullAdmins();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, canManageFullAdmins]);

  const editing = useMemo(() => rows.find((item) => String(item.id) === String(form.id)), [form.id, rows]);

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const updateFullAdminForm = (key, value) => {
    setFullAdminForm((current) => ({ ...current, [key]: value }));
  };

  const updatePermission = (key, value) => {
    setForm((current) => ({
      ...current,
      permissions: {
        ...current.permissions,
        [key]: value,
      },
    }));
  };

  const resetForm = () => {
    setForm({
      id: "",
      name: "",
      email: "",
      password: "",
      is_active: true,
      permissions: buildPermissionState(),
    });
    setLastSecret(null);
  };

  const editRow = (row) => {
    setForm({
      id: row.id,
      name: row.name || "",
      email: row.email || "",
      password: "",
      is_active: Number(row.is_active || 0) === 1,
      permissions: buildPermissionState(row.permissions || {}),
    });
    setLastSecret({
      secret: row.totp_secret || "",
      qr_url: row.totp_qr_url || "",
    });
  };

  const editFullAdmin = (row) => {
    setFullAdminForm({
      id: row.id,
      name: row.name || "",
      email: row.email || "",
      password: "",
      status: row.status || "Active",
      is_main_admin: Boolean(row.is_main_admin),
    });
  };

  const resetFullAdminForm = () => {
    setFullAdminForm({
      id: "",
      name: "",
      email: "",
      password: "",
      status: "Active",
      is_main_admin: false,
    });
  };

  const saveFullAdmin = async () => {
    if (!fullAdminForm.id) {
      alert("Select an admin account first");
      return;
    }
    if (!fullAdminForm.email.trim()) {
      alert("Enter admin email");
      return;
    }
    try {
      await API.put(`/admin/full-admins/${fullAdminForm.id}`, {
        name: fullAdminForm.name.trim(),
        email: fullAdminForm.email.trim(),
        password: fullAdminForm.password.trim(),
        status: fullAdminForm.status,
      });
      resetFullAdminForm();
      await loadFullAdmins();
      alert("Admin account updated");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to update admin account");
    }
  };

  const saveSubAdmin = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      alert("Enter name and email");
      return;
    }
    if (!form.id && !form.password.trim()) {
      alert("Enter password for new sub admin");
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password.trim(),
        is_active: form.is_active,
        permissions: form.permissions,
      };
      const res = form.id
        ? await API.put(`/admin/sub-admins/${form.id}`, payload)
        : await API.post("/admin/sub-admins", payload);
      resetForm();
      setLastSecret(res.data?.setup || null);
      await loadRows();
      alert(form.id ? "Sub admin updated" : "Sub admin created");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save sub admin");
    }
  };

  const runReset = async (mode) => {
    if (!confirmPassword.trim()) {
      alert("Enter admin password");
      return;
    }
    const messages = {
      bets: "Reset all bet records?",
      deposits: "Reset all deposit records?",
      withdraws: "Reset all withdrawal records?",
    };
    if (!window.confirm(messages[mode] || "Continue?")) return;

    setResetLoading(mode);
    try {
      await API.post("/admin/system/reset-records", {
        mode,
        confirm_password: confirmPassword,
      });
      setConfirmPassword("");
      alert("Reset completed");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to reset records");
    } finally {
      setResetLoading("");
    }
  };

  if (!canManage) {
    return (
      <AdminLayout>
        <section style={lockedPage}>
          <h1 style={title}>Sub Admin Access</h1>
          <p style={subtitle}>Only the main admin can create sub admins and run reset actions.</p>
        </section>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div>
            <h1 style={title}>Sub Admin Access</h1>
            <p style={subtitle}>Create sub admins, assign permissions, and track who approved actions.</p>
          </div>
          <button type="button" style={secondaryButton} onClick={resetForm}>New Sub Admin</button>
        </div>

        {canManageFullAdmins && (
          <section style={card}>
            <div style={sectionHeader}>
              <div>
                <h3 style={cardTitle}>Full Admin Control</h3>
                <p style={helpText}>Master admin: admin@gmail.com. Manage other full admin IDs from here.</p>
              </div>
              <button type="button" style={secondaryButton} onClick={loadFullAdmins}>Refresh</button>
            </div>

            <div style={adminControlGrid}>
              <div style={listWrap}>
                {fullAdminLoading ? <div style={empty}>Loading full admins...</div> : null}
                {!fullAdminLoading && fullAdminRows.map((row) => (
                  <button key={row.id} type="button" style={fullAdminForm.id === row.id ? activeAdminCard : rowCard} onClick={() => editFullAdmin(row)}>
                    <div>
                      <strong style={rowName}>{row.email}</strong>
                      <div style={rowMeta}>{row.name || `Admin ID ${row.id}`}</div>
                      <div style={rowMeta}>{row.is_main_admin ? "Main controller" : `Under admin@gmail.com - ID ${row.id}`}</div>
                    </div>
                    <div style={rowRight}>
                      <span style={rowStatus(String(row.status || "Active") !== "Disabled")}>{String(row.status || "Active") === "Disabled" ? "Disabled" : "Enabled"}</span>
                      <small style={rowMeta}>ID {row.id}</small>
                    </div>
                  </button>
                ))}
              </div>

              <div style={editPanel}>
                <h4 style={miniTitle}>{fullAdminForm.id ? `Edit Admin ID ${fullAdminForm.id}` : "Select Admin"}</h4>
                <label style={field}>
                  <span>Name</span>
                  <input style={input} value={fullAdminForm.name} onChange={(event) => updateFullAdminForm("name", event.target.value)} disabled={!fullAdminForm.id} />
                </label>
                <label style={field}>
                  <span>Email</span>
                  <input style={input} value={fullAdminForm.email} onChange={(event) => updateFullAdminForm("email", event.target.value)} disabled={!fullAdminForm.id || fullAdminForm.is_main_admin} />
                </label>
                <label style={field}>
                  <span>New Password</span>
                  <input style={input} type="password" placeholder="Leave blank to keep same" value={fullAdminForm.password} onChange={(event) => updateFullAdminForm("password", event.target.value)} disabled={!fullAdminForm.id} />
                </label>
                <label style={field}>
                  <span>Status</span>
                  <select style={input} value={fullAdminForm.status} onChange={(event) => updateFullAdminForm("status", event.target.value)} disabled={!fullAdminForm.id || fullAdminForm.is_main_admin}>
                    <option value="Active">Enabled</option>
                    <option value="Disabled">Disabled</option>
                  </select>
                </label>
                <button type="button" style={primaryButton} onClick={saveFullAdmin} disabled={!fullAdminForm.id}>
                  Save Full Admin
                </button>
              </div>
            </div>
          </section>
        )}

        <div style={grid}>
          <section style={card}>
            <h3 style={cardTitle}>{editing ? "Edit Sub Admin" : "Create Sub Admin"}</h3>
            <label style={field}>
              <span>Name</span>
              <input style={input} value={form.name} onChange={(event) => updateForm("name", event.target.value)} />
            </label>
            <label style={field}>
              <span>Email</span>
              <input style={input} value={form.email} onChange={(event) => updateForm("email", event.target.value)} />
            </label>
            <label style={field}>
              <span>{editing ? "New Password (optional)" : "Password"}</span>
              <input style={input} type="password" value={form.password} onChange={(event) => updateForm("password", event.target.value)} />
            </label>
            <label style={toggleRow}>
              <span>Active</span>
              <input type="checkbox" checked={form.is_active} onChange={(event) => updateForm("is_active", event.target.checked)} />
            </label>

            <div style={permissionGrid}>
              {ADMIN_PERMISSION_KEYS.map((key) => (
                <label key={key} style={permissionItem}>
                  <input
                    type="checkbox"
                    checked={Boolean(form.permissions[key])}
                    onChange={(event) => updatePermission(key, event.target.checked)}
                  />
                  <span>{permissionLabels[key] || key}</span>
                </label>
              ))}
            </div>

            <button type="button" style={primaryButton} onClick={saveSubAdmin}>
              {editing ? "Save Changes" : "Create Sub Admin"}
            </button>

            {lastSecret?.secret && (
              <div style={setupBox}>
                <strong>TOTP Setup</strong>
                <p style={setupText}>Give this QR or secret to the sub admin for Google Authenticator login.</p>
                {lastSecret.qr_url ? <img src={lastSecret.qr_url} alt="TOTP QR" style={qrImage} /> : null}
                <input style={input} readOnly value={lastSecret.secret} />
              </div>
            )}
          </section>

          <section style={card}>
            <h3 style={cardTitle}>Sub Admins</h3>
            <div style={listWrap}>
              {loading ? <div style={empty}>Loading...</div> : null}
              {!loading && !rows.length ? <div style={empty}>No sub admins yet.</div> : null}
              {rows.map((row) => (
                <button key={row.id} type="button" style={rowCard} onClick={() => editRow(row)}>
                  <div>
                    <strong style={rowName}>{row.name}</strong>
                    <div style={rowMeta}>{row.email}</div>
                    <div style={rowMeta}>Created by {row.created_by_name || "Admin"}</div>
                  </div>
                  <div style={rowRight}>
                    <span style={rowStatus(Number(row.is_active || 0) === 1)}>{Number(row.is_active || 0) === 1 ? "Active" : "Disabled"}</span>
                    <small style={rowMeta}>{Object.entries(row.permissions || {}).filter(([, value]) => value).length} permissions</small>
                  </div>
                </button>
              ))}
            </div>
          </section>
        </div>

        <section style={card}>
          <h3 style={cardTitle}>Reset Records</h3>
          <p style={helpText}>This clears records only. It does not reverse already-settled wallet balances.</p>
          <label style={field}>
            <span>Admin Password Confirmation</span>
            <input style={input} type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
          </label>
          <div style={resetActions}>
            <button type="button" style={dangerButton} disabled={resetLoading === "bets"} onClick={() => runReset("bets")}>
              {resetLoading === "bets" ? "Resetting..." : "Reset Bets"}
            </button>
            <button type="button" style={dangerButton} disabled={resetLoading === "deposits"} onClick={() => runReset("deposits")}>
              {resetLoading === "deposits" ? "Resetting..." : "Reset Deposits"}
            </button>
            <button type="button" style={dangerButton} disabled={resetLoading === "withdraws"} onClick={() => runReset("withdraws")}>
              {resetLoading === "withdraws" ? "Resetting..." : "Reset Withdraws"}
            </button>
          </div>
        </section>
      </section>
    </AdminLayout>
  );
}

const page = { display: "grid", gap: 18 };
const lockedPage = { display: "grid", gap: 12 };
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 };
const sectionHeader = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" };
const title = { margin: 0, fontSize: 24, fontWeight: 900 };
const subtitle = { margin: "6px 0 0", color: "#64748b" };
const grid = { display: "grid", gridTemplateColumns: "1.1fr .9fr", gap: 18 };
const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 22, padding: 22, display: "grid", gap: 14 };
const cardTitle = { margin: 0, fontSize: 18, fontWeight: 900 };
const adminControlGrid = { display: "grid", gridTemplateColumns: "1fr 360px", gap: 14, alignItems: "start" };
const editPanel = { display: "grid", gap: 12, padding: 14, border: "1px solid #e2e8f0", borderRadius: 16, background: "#f8fafc" };
const miniTitle = { margin: 0, fontSize: 15, fontWeight: 900, color: "#0f172a" };
const field = { display: "grid", gap: 8, color: "#334155", fontWeight: 800 };
const input = { height: 48, borderRadius: 12, border: "1px solid #cbd5e1", padding: "0 14px", fontSize: 14 };
const toggleRow = { display: "flex", alignItems: "center", justifyContent: "space-between", color: "#334155", fontWeight: 800 };
const permissionGrid = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 };
const permissionItem = { display: "flex", alignItems: "center", gap: 8, minHeight: 38, padding: "0 10px", borderRadius: 12, background: "#f8fafc", border: "1px solid #e2e8f0", color: "#334155", fontSize: 13 };
const primaryButton = { height: 48, border: 0, borderRadius: 14, background: "#316ce8", color: "#fff", fontWeight: 900, cursor: "pointer" };
const secondaryButton = { height: 42, border: "1px solid #bfdbfe", borderRadius: 12, background: "#eff6ff", color: "#2563eb", fontWeight: 800, padding: "0 14px", cursor: "pointer" };
const setupBox = { display: "grid", gap: 10, padding: 14, borderRadius: 16, background: "#f8fafc", border: "1px solid #e2e8f0" };
const setupText = { margin: 0, color: "#64748b", fontSize: 13, lineHeight: 1.45 };
const qrImage = { width: 180, height: 180, borderRadius: 14, background: "#fff", border: "1px solid #e2e8f0", padding: 8 };
const listWrap = { display: "grid", gap: 10 };
const empty = { padding: 18, borderRadius: 14, background: "#f8fafc", color: "#64748b", textAlign: "center" };
const rowCard = { width: "100%", border: "1px solid #e2e8f0", borderRadius: 16, background: "#fff", padding: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left" };
const activeAdminCard = { ...rowCard, border: "1px solid #2563eb", background: "#eff6ff", boxShadow: "0 10px 24px rgba(37,99,235,.12)" };
const rowName = { color: "#0f172a", fontSize: 15 };
const rowMeta = { color: "#64748b", fontSize: 12, marginTop: 4 };
const rowRight = { display: "grid", gap: 6, justifyItems: "end" };
const rowStatus = (active) => ({ padding: "6px 10px", borderRadius: 999, background: active ? "#dcfce7" : "#fee2e2", color: active ? "#15803d" : "#b91c1c", fontSize: 12, fontWeight: 900 });
const helpText = { margin: 0, color: "#64748b", fontSize: 13 };
const resetActions = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 };
const dangerButton = { height: 48, border: 0, borderRadius: 14, background: "#dc2626", color: "#fff", fontWeight: 900, cursor: "pointer" };

export default AdminSubAdmins;
